/**
 * RTDB Sync Service - Sincronización bidireccional entre IndexedDB y Firebase RTDB
 * Maneja descarga inicial, actualizaciones incrementales y sincronización offline
 */

import { getDatabase, ref, get, onValue, off, query, orderByChild, equalTo, limitToLast, serverTimestamp } from 'firebase/database';
import { database } from '../firebase';
import indexedDBService, { STORES } from './indexedDBService';

class RTDBSyncService {
    constructor() {
        this.db = database;
        this.listeners = new Map();
        this.syncInProgress = new Set();
        this.lastSyncTimestamps = new Map();
        this.isOnline = navigator.onLine;
        this.syncInterval = null;
        this.setupOnlineListener();
    }

    setupOnlineListener() {
        window.addEventListener('online', () => {
            this.isOnline = true;
            console.log('[RTDBSync] Conexión restaurada - iniciando sincronización');
            this.syncAllPending();
        });
        window.addEventListener('offline', () => {
            this.isOnline = false;
            console.log('[RTDBSync] Sin conexión - modo offline activado');
        });
    }

    // Verificar conexión
    async checkConnection() {
        try {
            const testRef = ref(this.db, '.info/connected');
            const snapshot = await get(testRef);
            this.isOnline = snapshot.val() === true;
            return this.isOnline;
        } catch {
            this.isOnline = false;
            return false;
        }
    }

    // Sincronización completa de datos estáticos (áreas, temas, preguntas)
    async syncStaticData(force = false) {
        if (!this.isOnline && !force) {
            console.log('[RTDBSync] Offline - usando datos locales');
            return false;
        }

        if (this.syncInProgress.has('static')) {
            console.log('[RTDBSync] Sincronización estática ya en progreso');
            return false;
        }

        this.syncInProgress.add('static');
        console.log('[RTDBSync] Iniciando sincronización de datos estáticos...');

        try {
            // Verificar versiones remotas
            const versions = await this.fetchRemoteVersions();
            
            // Sincronizar áreas
            if (force || await this.shouldSync('areas', versions.areas)) {
                await this.syncAreas();
            }

            // Sincronizar temas
            if (force || await this.shouldSync('temas', versions.temas)) {
                await this.syncTemas();
            }

            // Sincronizar preguntas por tema
            if (force || await this.shouldSync('preguntas_por_tema', versions.preguntas)) {
                await this.syncPreguntasPorTema();
            }

            this.lastSyncTimestamps.set('static', Date.now());
            console.log('[RTDBSync] Sincronización estática completada');
            return true;
        } catch (error) {
            console.error('[RTDBSync] Error en sincronización estática:', error);
            throw error;
        } finally {
            this.syncInProgress.delete('static');
        }
    }

    async fetchRemoteVersions() {
        try {
            const versionsRef = ref(this.db, 'cache_control');
            const snapshot = await get(versionsRef);
            if (snapshot.exists()) {
                return snapshot.val();
            }
        } catch (error) {
            console.warn('[RTDBSync] No se pudieron obtener versiones remotas:', error);
        }
        return { areas: null, temas: null, preguntas: null };
    }

    async shouldSync(storeName, remoteVersion) {
        if (!remoteVersion) return true; // Si no hay versión remota, sincronizar
        const localMeta = await indexedDBService.getCacheMeta(`${storeName}_version`);
        return !localMeta || localMeta.version !== remoteVersion;
    }

    // Sincronizar áreas
    async syncAreas() {
        console.log('[RTDBSync] Sincronizando áreas...');
        const areasRef = ref(this.db, 'areas');
        const snapshot = await get(areasRef);
        
        if (snapshot.exists()) {
            const areas = [];
            snapshot.forEach(child => {
                areas.push({
                    id: parseInt(child.key),
                    ...child.val()
                });
            });
            await indexedDBService.saveAreas(areas, this.lastSyncTimestamps.get('areas_version'));
            console.log(`[RTDBSync] ${areas.length} áreas sincronizadas`);
            return areas;
        }
        return [];
    }

    // Sincronizar temas
    async syncTemas() {
        console.log('[RTDBSync] Sincronizando temas...');
        const temasRef = ref(this.db, 'temas');
        const snapshot = await get(temasRef);
        
        if (snapshot.exists()) {
            const temas = [];
            snapshot.forEach(child => {
                temas.push({
                    id: parseInt(child.key),
                    ...child.val()
                });
            });
            await indexedDBService.saveTemas(temas, this.lastSyncTimestamps.get('temas_version'));
            console.log(`[RTDBSync] ${temas.length} temas sincronizados`);
            return temas;
        }
        return [];
    }

    // Sincronizar preguntas por tema
    async syncPreguntasPorTema() {
        console.log('[RTDBSync] Sincronizando preguntas_por_tema...');
        const preguntasRef = ref(this.db, 'preguntas_por_tema');
        const snapshot = await get(preguntasRef);
        
        if (snapshot.exists()) {
            const data = snapshot.val();
            await indexedDBService.savePreguntasPorTemaMap(data, this.lastSyncTimestamps.get('preguntas_version'));
            
            let total = 0;
            for (const temaId of Object.keys(data)) {
                total += Object.keys(data[temaId] || {}).length;
            }
            console.log(`[RTDBSync] ${total} preguntas sincronizadas en ${Object.keys(data).length} temas`);
            return total;
        }
        return 0;
    }

    // Obtener datos con fallback a IndexedDB
    async getAreas() {
        // Intentar obtener de IndexedDB primero
        const local = await indexedDBService.getAreas();
        if (local && local.length > 0) {
            return { data: local, source: 'local' };
        }
        
        // Si no hay datos locales, intentar RTDB
        if (this.isOnline) {
            try {
                const areas = await this.syncAreas();
                return { data: areas, source: 'remote' };
            } catch (error) {
                console.error('[RTDBSync] Error obteniendo áreas:', error);
            }
        }
        return { data: [], source: 'none' };
    }

    async getTemas() {
        const local = await indexedDBService.getTemas();
        if (local && local.length > 0) {
            return { data: local, source: 'local' };
        }
        
        if (this.isOnline) {
            try {
                const temas = await this.syncTemas();
                return { data: temas, source: 'remote' };
            } catch (error) {
                console.error('[RTDBSync] Error obteniendo temas:', error);
            }
        }
        return { data: [], source: 'none' };
    }

    async getTemasByArea(areaId) {
        const local = await indexedDBService.getTemasByArea(areaId);
        if (local && local.length > 0) {
            return { data: local, source: 'local' };
        }
        
        if (this.isOnline) {
            try {
                const temasRef = ref(this.db, 'temas');
                const q = query(temasRef, orderByChild('area_id'), equalTo(areaId));
                const snapshot = await get(q);
                if (snapshot.exists()) {
                    const temas = [];
                    snapshot.forEach(child => {
                        temas.push({ id: parseInt(child.key), ...child.val() });
                    });
                    return { data: temas, source: 'remote' };
                }
            } catch (error) {
                console.error('[RTDBSync] Error obteniendo temas por área:', error);
            }
        }
        return { data: [], source: 'none' };
    }

    async getPreguntasPorTema(temaId) {
        const local = await indexedDBService.getPreguntasPorTema(temaId);
        if (local && local.length > 0) {
            return { data: local, source: 'local' };
        }
        
        if (this.isOnline) {
            try {
                const preguntasRef = ref(this.db, `preguntas_por_tema/${temaId}`);
                const snapshot = await get(preguntasRef);
                if (snapshot.exists()) {
                    const preguntas = [];
                    snapshot.forEach(child => {
                        const q = child.val();
                        preguntas.push({ id: child.key, ...q, tema_id: temaId });
                    });
                    return { data: preguntas, source: 'remote' };
                }
            } catch (error) {
                console.error('[RTDBSync] Error obteniendo preguntas:', error);
            }
        }
        return { data: [], source: 'none' };
    }

    // Suscripción en tiempo real para datos dinámicos
    subscribeToPath(path, callback, options = {}) {
        const { 
            queryConstraints = [], 
            onlyOnce = false,
            localFirst = true 
        } = options;

        // Primero intentar datos locales
        if (localFirst) {
            this.getLocalDataForPath(path).then(localData => {
                if (localData && localData.length > 0) {
                    callback(localData, 'local');
                }
            });
        }

        if (!this.isOnline) {
            console.log('[RTDBSync] Offline - solo datos locales disponibles');
            return () => {};
        }

        try {
            let dbRef = ref(this.db, path);
            if (queryConstraints.length > 0) {
                dbRef = query(dbRef, ...queryConstraints);
            }

            const listener = onlyOnce ? 'once' : 'onValue';
            const unsubscribe = onlyOnce 
                ? get(dbRef).then(snap => {
                    if (snap.exists()) {
                        const data = [];
                        snap.forEach(child => data.push({ id: child.key, ...child.val() }));
                        callback(data, 'remote');
                    }
                    return () => {};
                })
                : onValue(dbRef, (snapshot) => {
                    if (snapshot.exists()) {
                        const data = [];
                        snapshot.forEach(child => data.push({ id: child.key, ...child.val() }));
                        callback(data, 'remote');
                    } else {
                        callback([], 'remote');
                    }
                }, { onlyOnce: false });

            const key = `${path}_${Date.now()}`;
            this.listeners.set(key, { path, unsubscribe: typeof unsubscribe === 'function' ? unsubscribe : () => off(dbRef) });
            
            return () => this.unsubscribe(key);
        } catch (error) {
            console.error('[RTDBSync] Error suscribiendo a:', path, error);
            return () => {};
        }
    }

    unsubscribe(key) {
        const listener = this.listeners.get(key);
        if (listener) {
            try {
                if (typeof listener.unsubscribe === 'function') {
                    listener.unsubscribe();
                }
            } catch (e) {
                console.warn('[RTDBSync] Error desuscribiendo:', e);
            }
            this.listeners.delete(key);
        }
    }

    unsubscribeAll() {
        for (const [key, listener] of this.listeners) {
            try {
                if (typeof listener.unsubscribe === 'function') {
                    listener.unsubscribe();
                }
            } catch (e) {
                console.warn('[RTDBSync] Error desuscribiendo:', e);
            }
        }
        this.listeners.clear();
    }

    async getLocalDataForPath(path) {
        // Mapear paths a métodos de IndexedDB
        if (path === 'areas') return indexedDBService.getAreas();
        if (path === 'temas') return indexedDBService.getTemas();
        if (path.startsWith('temas/area_')) {
            const areaId = parseInt(path.split('_')[1]);
            return indexedDBService.getTemasByArea(areaId);
        }
        if (path.startsWith('preguntas_por_tema/')) {
            const temaId = parseInt(path.split('/')[1]);
            return indexedDBService.getPreguntasPorTema(temaId);
        }
        return null;
    }

    // Sincronizar datos pendientes (resultados de quiz, etc.)
    async syncAllPending() {
        if (!this.isOnline) return;

        console.log('[RTDBSync] Sincronizando datos pendientes...');
        
        // Sincronizar resultados de quiz
        await this.syncQuizResults();
        
        // Sincronizar otras colas pendientes
        await this.syncPendingQueue();
    }

    async syncQuizResults() {
        // Obtener usuarios autenticados (esto requeriría acceso al auth)
        // Por ahora, sincronizar todos los resultados no sincronizados
        // En producción, filtrar por usuario actual
    }

    async syncPendingQueue() {
        const pending = await indexedDBService.getPendingSync();
        for (const item of pending) {
            if (item.status === 'failed') continue;
            try {
                await this.processSyncItem(item);
                await indexedDBService.markSyncComplete(item.id);
            } catch (error) {
                console.error('[RTDBSync] Error procesando item pendiente:', item.id, error);
                await indexedDBService.markSyncFailed(item.id, error);
            }
        }
    }

    async processSyncItem(item) {
        // Procesar según tipo
        switch (item.type) {
            case 'quiz_result':
                // Enviar a RTDB
                break;
            case 'user_progress':
                // Actualizar progreso
                break;
            default:
                console.log('[RTDBSync] Tipo de sync desconocido:', item.type);
        }
    }

    // Iniciar sincronización periódica
    startPeriodicSync(intervalMs = 5 * 60 * 1000) {
        if (this.syncInterval) return;
        
        this.syncInterval = setInterval(() => {
            if (this.isOnline) {
                this.syncStaticData(false).catch(console.error);
            }
        }, intervalMs);
        
        console.log('[RTDBSync] Sincronización periódica iniciada cada', intervalMs / 1000, 'segundos');
    }

    stopPeriodicSync() {
        if (this.syncInterval) {
            clearInterval(this.syncInterval);
            this.syncInterval = null;
            console.log('[RTDBSync] Sincronización periódica detenida');
        }
    }

    // Forzar actualización completa
    async forceFullSync() {
        console.log('[RTDBSync] Forzando sincronización completa...');
        await this.syncStaticData(true);
        await this.syncAllPending();
    }

    // Obtener estado de sincronización
    getSyncStatus() {
        return {
            isOnline: this.isOnline,
            lastStaticSync: this.lastSyncTimestamps.get('static'),
            pendingCount: this.syncInProgress.size
        };
    }
}

// Singleton
const rtdbSyncService = new RTDBSyncService();

export default rtdbSyncService;